import React, { useState } from "react";
import { useInvitation } from "../sevices/hooks/admin";
import PaginationComp from "./dashboard-components/paginate";

interface AccountData {
  dateCreated: string;
  email: string;
  firstName: string;
  id: string;
  InvitedBy: string;
  lastName: string;
  lastEmailSent: string;
  phoneNo: string;
}

interface AccountsTableProps {
  data?: {
    data: AccountData[];
    page: number;
    pageSize: number;
    total: string;
    totalPages: number;
  };
  isLoading: boolean;
  page: number;
  onPageChange: (page: number) => void;
}

// Simple Modal Component
const Modal = ({ isOpen, onClose, onConfirm, email }: any) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirm Re-invitation</h3>
        <p className="text-gray-600 mb-6">
          Are you sure you want to re-send the invitation to {email}?
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-[#0eb683] text-white rounded-md hover:bg-[#0ca575] transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

const InvitationTable = ({
  data,
  isLoading,
  page,
  onPageChange,
}: AccountsTableProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState("");
  const reInvite = useInvitation(selectedEmail);

  const handleReInvite = (email: string) => {
    setSelectedEmail(email);
    setModalOpen(true);
  };

  const handleConfirmReInvite = async () => {
    try {
      const res = await reInvite.mutateAsync({});
      setModalOpen(false);
      // You might want to add some success notification here
    } catch (error) {
      console.error("Failed to send invitation:", error);
      // You might want to add error handling here
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (isLoading) {
    return <div className="flex justify-center p-4">Loading...</div>;
  }

  const columns = [
    { key: "sn", label: "S/N", align: "left" },
    { key: "refId", label: "DATE INVITED", align: "left" },
    { key: "full_name", label: "FULL NAME", align: "left" },
    { key: "referred_by", label: "REFERRED BY", align: "left" },
    { key: "email", label: "EMAIL ADDRESS", align: "left" },
    { key: "phone", label: "PHONE NUMBER", align: "left" },
    { key: "last_email", label: "LAST EMAIL SENT", align: "left" },
    { key: "actions", label: "ACTIONS", align: "center" },
  ];

  // Handle the case when data is undefined
  if (!data) {
    return <div className="flex justify-center p-4">No data available</div>;
  }
  const startIndex = (page - 1) * 10;
  return (
    <div className="bg-white rounded-lg w-full">
      <div className="overflow-x-auto">
        <table className="border-collapse appearance-none bg-white min-w-full mb-6">
          <thead className="bg-[#fff] appearance-none">
            <tr className="border-b bg-gray-50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`font-normal text-sm text-primary py-4 whitespace-nowrap px-4 border-r border-slate-50 ${
                    column.align === "center"
                      ? "text-center"
                      : `text-${column.align}`
                  }`}
                >
                  <span className="text-[#5C6F7F] font-bold text-[12px] leading-[18px]">
                    {column.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="mt-3 pt-3 w-full space-y-8 border-r border-slate-50">
            {data.data.map((invitation: AccountData, index: number) => {
              const serialNumber = startIndex + index + 1;
              return (
                <tr
                  key={invitation.id + index}
                  className="appearance-none my-4 border border-slate-50 even:bg-white odd:bg-slate-100"
                >
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {serialNumber}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    <div className="truncate">
                      {formatDate(invitation.dateCreated)}
                    </div>
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {invitation.firstName || "N/A"}{" "}
                    {invitation.lastName || "N/A"}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {invitation?.InvitedBy || "N/A"}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {invitation.email || "N/A"}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {invitation?.phoneNo || "N/A"}
                  </td>
                  <td className="font-normal text-sm text-[#202223] py-4 px-4">
                    {formatDate(invitation?.lastEmailSent)}
                  </td>
                  <td className="py-3 px-4 text-center">
                    <button
                      onClick={() => handleReInvite(invitation.email)}
                      className="inline-flex items-center px-3 py-1 text-sm hover:bg-gray-100 rounded-md transition-colors border border-[#0eb683] text-[#0eb683]"
                    >
                      Re-Invite
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirmReInvite}
        email={selectedEmail}
      />

      {data && (
        <div className="px-4">
          <PaginationComp
            currentPage={page}
            totalPages={data.totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </div>
  );
};

export default InvitationTable;
